import {
  Modal,
  Select,
  Button,
  Stack,
  Group,
  Input,
  Text,
  SegmentedControl,
} from "@mantine/core";
import { useState } from "react";
import countries from "./countries.json";
import { useTranslation } from "react-i18next";
import useReportStore from "../../../store/useReportStore";

import { PartialProps } from "../../../types/api";

type ReportCreationModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const ReportCreationModal = ({
  opened,
  onClose,
}: ReportCreationModalProps) => {
  const [country, setCountry] = useState<string | null>(null);
  const [profession, setProfession] = useState("");

  const [changeSection, setChangeSection] = useState(true);
  const { t } = useTranslation();

  const { generateReport, makePartialReport, reportId, report } =
    useReportStore();

  const positions = report.optimal_positions.mark_down !== null;

  const handleGenerate = async () => {
    const sections = positions
      ? ["optimal_positions"]
      : ["optimal_positions_switch", "skills_to_master_switch_role"];

    if (changeSection) {
      const props: PartialProps = {
        credentials: {
          sections: sections,
          ctx_overrides: {
            location: country ? country : undefined,
            switch_to: profession === "" ? null : profession,
          },
        },
        reportId,
      };

      await makePartialReport(props);
    } else {
      await generateReport({
        profession,
        country: country ? country : undefined,
        id: reportId,
      });
    }
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setCountry(null);
        setProfession("");
        onClose();
      }}
      title={
        <Text size="18px" mb={4}>
          {t("generate_report_modal.title")}
        </Text>
      }
      radius="md"
      size="md"
      centered
    >
      <Stack gap="md">
        <Select
          label={
            <Text size="16px" mb={10}>
              {t("generate_report_modal.select_country_label")}
            </Text>
          }
          placeholder={t("generate_report_modal.select_country_placeholder")}
          searchable
          clearable
          data={countries}
          value={country}
          onChange={setCountry}
        />
        {!positions && (
          <Input.Wrapper
            label={
              <Text size="16px">
                {t("generate_report_modal.profession_label")}
              </Text>
            }
          >
            <Input
              placeholder={t("generate_report_modal.profession_placeholder")}
              value={profession}
              onChange={(event) => setProfession(event.currentTarget.value)}
            />
          </Input.Wrapper>
        )}

        <div style={{ width: "100%" }}>
          <Text size="16px" fw={500} mb={4}>
            {t("reportModal.title")}
          </Text>
          <SegmentedControl
            value={changeSection ? "section" : "report"}
            onChange={(val) => setChangeSection(val === "section")}
            data={[
              { label: t("reportModal.section"), value: "section" },
              { label: t("reportModal.report"), value: "report" },
            ]}
            radius="md"
            size="sm"
            color="violet"
            style={{ width: "100%" }}
            mt="md"
          />
        </div>
        <Group justify="flex-end" mt="md">
          <Button
            variant="default"
            onClick={() => {
              setCountry(null);
              setProfession("");
              onClose();
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!(country || profession.trim())}
          >
            {t("generate_report")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
